const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/components/AppBar/TextArea/TextArea.tsx';
import Imm from 'immutable';
import * as React from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import { createSelector } from 'reselect';
import Typography from '@mui/material/Typography';
import * as Styles from './styles';
import * as PatSelectors from '@cistec/common_web/selectors/patientsSelectors';
import { useSelector } from 'react-redux';

/**
 * React props
 */
export interface ITextAreaProps {
    patNameKind?: 'primary' | 'secondary' | 'never'; // 'never' by default
    primaryText?: string | MessageDescriptor;
    secondaryText?: string | MessageDescriptor;
    scaleFactor?: number; // if this is set, the text will be scaled
    marginBottom?: number; // if this is set, the text area will align itself to margin-bottom
    headerElement?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'; // if empty h6 is used
    primaryTextStyle?: React.CSSProperties;
}

interface ITextAreaData {
    patient: Imm.Map<string, any>;
}

const textAreaData = createSelector(
    [PatSelectors.selectCurrentPatData],
    (patient: Imm.Map<string, any>): ITextAreaData => ({
        patient,
    })
);

export const TextArea = (props: ITextAreaProps) => {
    const { patient } = useSelector(textAreaData);

    const getPatName = () => {
        if (!patient) {
            return '';
        } else {
            const titel = patient.get('TITEL');
            const name = patient.get('NAME');
            const vorname = patient.get('VORNAME');
            return `${titel || ''} ${name || ''} ${vorname || ''}`.trim();
        }
    };

    const getPrimaryText = (patName: string) => {
        if (props.primaryText) {
            if (typeof props.primaryText === 'string') {
                return props.primaryText;
            } else {
                return <FormattedMessage {...props.primaryText} />;
            }
        } else if (props.patNameKind === 'primary') {
            return patName;
        } else {
            return '';
        }
    };

    const getSecondaryText = (patName: string) => {
        if (props.secondaryText) {
            if (typeof props.secondaryText === 'string') {
                return props.secondaryText;
            } else {
                return <FormattedMessage {...props.secondaryText} />;
            }
        } else if (props.patNameKind === 'secondary') {
            return patName;
        } else {
            return '';
        }
    };

    const patName = getPatName();
    const primaryText = getPrimaryText(patName);
    const secondaryText = getSecondaryText(patName);
    const hasMarginBottom = typeof props.marginBottom === 'number';

    let primaryTextStyle: React.CSSProperties = Styles.primaryTextStyle;
    if (props?.primaryTextStyle) {
        primaryTextStyle = { ...Styles.primaryTextStyle, ...props.primaryTextStyle };
    }

    const textAreaInlineStyle: React.CSSProperties = {
        alignSelf: !hasMarginBottom ? undefined : 'flex-end',
        marginBottom: props.marginBottom,
        transform: (props.scaleFactor || 1) === 1 ? undefined : `scale(${props.scaleFactor})`,
    };
    return (
        <div className={Styles.textAreaStyle} style={textAreaInlineStyle}>
            <Typography variant={props.headerElement ?? 'h6'} style={primaryTextStyle}>
                {primaryText || '\u00a0'}
            </Typography>
            {!!secondaryText && (
                <Typography variant="subtitle1" style={Styles.secondaryTextStyle}>
                    {secondaryText}
                </Typography>
            )}
        </div>
    );
};
